import React, {useMemo, useState} from 'react';
import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {deletePresentationTheme, fetchPresentationThemeListService} from '../../../../service/presentationTheme.service';
import {FONT_SIZE, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {IUpdateError} from '../../../../store/actions/types/error.types';
import {TOOLTIP_DELAY} from '../../../../constants';
import TooltipWrapper from '../../../components/common/TooltipWrapper';
import PresentationThemeAllowedUsersModal from './PresentationThemeAllowedUsersModal';
import PresentationThemeModal, {PresentationThemeModalMode} from './PresentationThemeModal';
import {SORT} from '../../../../incase-domain/common/sort.enum';
import Button from '../../../../incase-domain/components/button/Button';
import DataView, {ViewMode} from '../../../components/common/data/DataView';
import {useListParamsState} from '../../../../hooks/useListParamsState';
import {createDataViewListStateProps} from '../../../../hooks/listParams';
import {prettyDateFormat} from '../../../../utils/dateTimeUtils';
import ConfirmModal from '../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';

interface ThemeModalState {
    open: boolean;
    mode: PresentationThemeModalMode;
    themeId?: string;
    readOnly?: boolean;
}

function PresentationThemeList(): JSX.Element {
    const [openModalDeleteTheme, setOpenModalDeleteTheme] = useState<boolean>(false);
    const [deleteTheme, setDeleteTheme] = useState<IPresentationTheme | null>(null);
    const [themeModal, setThemeModal] = useState<ThemeModalState>({open: false, mode: 'create'});

    const {t} = useTranslation();
    const loggedInUser = useSelector(loggedInUserSelector);

    const initialFontSizes = [
        {size: FONT_SIZE.EXTRA_SMALL, value: 0.5},
        {size: FONT_SIZE.SMALL, value: 1},
        {size: FONT_SIZE.MEDIUM, value: 1.5},
        {size: FONT_SIZE.LARGE, value: 2},
        {size: FONT_SIZE.EXTRA_LARGE, value: 2.5},
    ];

    const client = useQueryClient();
    const [selectedTheme, setSelectedTheme] = useState<IPresentationTheme>({
        _id: '',
        themeName: '',
        fontSizes: initialFontSizes,
        fonts: [],
        defaultFonts: [],
        lastModified: 0,
    });
    const [editUserAccessModal, setEditUserAccessModal] = useState<boolean>(false);

    const {listParams, setListParams} = useListParamsState();
    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    const {data, isFetching, isLoading}: UseQueryResult<IPresentationTheme[]> = useQuery<IPresentationTheme[]>({
        queryKey: QUERY_KEYS.theme.list({sortBy: listParams.sortBy}),
        queryFn: () => fetchPresentationThemeListService(listParams.sortBy),
        keepPreviousData: true,
    });

    const initialsAllowedUserIds = useMemo(() => {
        if (!data) return [];

        return data?.find((theme) => theme._id === selectedTheme._id)?.allowedUserIds || [];
    }, [data, selectedTheme._id]);

    const removePresentationTheme: UseMutationResult<boolean, IUpdateError, string> = useMutation(
        (themeId: string) => deletePresentationTheme(themeId),
        {
            onSuccess: () =>
                client
                    .invalidateQueries({queryKey: QUERY_KEYS.theme.lists()})
                    .then(() => toast.success(i18n.t('presentationThemesPage.toasts.presThemeDeleteSuccess'))),
        }
    );

    const openCreateThemeModal = (): void => {
        setThemeModal({open: true, mode: 'create'});
    };

    const isThemeReadOnly = (theme: IPresentationTheme): boolean => theme.ownerId !== loggedInUser?._id;

    const openEditThemeModal = (theme: IPresentationTheme): void => {
        setThemeModal({
            open: true,
            mode: 'edit',
            themeId: theme._id,
            readOnly: isThemeReadOnly(theme),
        });
    };

    const closeThemeModal = (): void => {
        setThemeModal((prev) => ({...prev, open: false}));
    };

    const modelContent = useMemo(() => {
        if (!deleteTheme?.presentations?.length) {
            return t('roomsPage.modals.deleteThemeConfirm', {
                themeName: deleteTheme?.themeName,
            });
        }

        if (deleteTheme.presentations?.length === 1) {
            return t('roomsPage.modals.deleteThemeSingleWarning', {
                presentationName: deleteTheme.presentations[0].name,
            });
        }

        return t('roomsPage.modals.deleteThemeMultipleWarning', {
            presentationsNumber: deleteTheme.presentations.length,
        });
    }, [deleteTheme?.presentations, deleteTheme?.themeName, t]);

    const renderThemeName = (theme: IPresentationTheme): JSX.Element => {
        return <>{theme.themeName}</>;
    };

    const renderLastModified = (theme: IPresentationTheme): JSX.Element => {
        return <>{prettyDateFormat(new Date(theme.lastModified), i18n.language)}</>;
    };

    const renderActions = (theme: IPresentationTheme): JSX.Element => {
        const readOnly = isThemeReadOnly(theme);

        return (
            <div
                className="e-table__actions"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <TooltipWrapper
                    id="room-editRoom"
                    content={readOnly ? t('presentationThemesPage.viewTheme') : t('presentationThemesPage.editTheme')}
                    place="left"
                    delayShow={TOOLTIP_DELAY}
                >
                    <Button
                        variant="ghost"
                        size="sm"
                        leading={readOnly ? 'solar:eye-linear' : 'solar:pen-2-linear'}
                        square
                        key={`${theme.themeName}-edit`}
                        onClick={() => openEditThemeModal(theme)}
                    />
                </TooltipWrapper>
                {!readOnly && (
                    <>
                        <TooltipWrapper
                            id="presentationTheme-access"
                            content={`${t('presentationThemesPage.allowUsers')}`}
                            place="left"
                            delayShow={TOOLTIP_DELAY}
                        >
                            <Button
                                variant="ghost"
                                size="sm"
                                leading="solar:users-group-rounded-linear"
                                square
                                onClick={() => {
                                    setSelectedTheme(theme);
                                    setEditUserAccessModal(true);
                                }}
                            />
                        </TooltipWrapper>
                        <TooltipWrapper
                            id="room-delete"
                            content={t('presentationThemesPage.deleteTheme')}
                            place="left"
                            delayShow={TOOLTIP_DELAY}
                        >
                            <Button
                                variant="ghost"
                                size="sm"
                                leading="solar:trash-bin-trash-linear"
                                square
                                color="error"
                                onClick={() => {
                                    setOpenModalDeleteTheme(true);
                                    setDeleteTheme(theme);
                                }}
                                key={`${theme.themeName}-delete`}
                            />
                        </TooltipWrapper>
                    </>
                )}
            </div>
        );
    };

    return (
        <>
            <DataView
                views={[
                    {
                        columns: [
                            {
                                label: t('presentationThemesPage.name'),
                                key: 'themeName',
                                direction: listParams.sortBy?.themeName || SORT.DESC,
                                sortable: true,
                                body: (row) => renderThemeName(row),
                            },
                            {
                                label: t('roomsPage.table.lastModified'),
                                key: 'lastModified',
                                direction: listParams.sortBy?.lastModified || SORT.DESC,
                                sortable: true,
                                body: (row) => renderLastModified(row),
                                width: '11rem',
                            },
                            {
                                body: (row) => renderActions(row),
                                width: '4rem',
                            },
                        ],
                        view: ViewMode.TABLE,
                    },
                ]}
                rows={data || []}
                rowIdentifier={(row) => row._id || ''}
                loading={isFetching || isLoading}
                onRowClick={(row) => {
                    openEditThemeModal(row);
                }}
                title={t('presentationThemesPage.presentationThemes')}
                headerActionPrimary={{
                    icon: 'ph:plus-bold',
                    onClick: openCreateThemeModal,
                    title: t('genericTable.new'),
                }}
                {...dataViewListState}
                inlineTitle
            />
            <PresentationThemeModal
                isOpen={themeModal.open}
                onClose={closeThemeModal}
                mode={themeModal.mode}
                themeId={themeModal.themeId}
                readOnly={themeModal.readOnly}
            />
            <PresentationThemeAllowedUsersModal
                isOpen={editUserAccessModal}
                theme={selectedTheme}
                setTheme={setSelectedTheme}
                setEditUserAccessModal={setEditUserAccessModal}
                initialAllowedUserIds={initialsAllowedUserIds}
            />
            <ConfirmModal
                title={
                    !deleteTheme?.presentations?.length
                        ? t('roomsPage.modals.deleteThemeConfirmHeader')
                        : t('roomsPage.modals.deleteThemeWarningHeader')
                }
                message={modelContent}
                color={deleteTheme?.presentations?.length ? 'warning' : 'error'}
                isOpen={openModalDeleteTheme}
                onCancel={() => setOpenModalDeleteTheme(false)}
                onConfirm={
                    !deleteTheme?.presentations?.length
                        ? () => {
                              removePresentationTheme.mutate(deleteTheme?._id || '');
                              setOpenModalDeleteTheme(false);
                              setDeleteTheme(null);
                          }
                        : undefined
                }
                showCancel
                showConfirm={!deleteTheme?.presentations?.length}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
            />
        </>
    );
}

export default PresentationThemeList;
